// ===================================================================
// 💰 GERENCIADOR DE PAGAMENTOS - VERSÃO SIMPLIFICADA
// ===================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { formatDate, formatCurrency, exportToCSV } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';
import { getPaymentStatusLabel, getStatusColor, getStatusBadgeStyle, getButtonStyle, ACTION_ICONS } from '../../constants/statusTranslations';
import { useAdminProcess } from '../../contexts/AdminProcessContext';
import { parseAdminSelectedProcessId } from '../../utils/adminProcessId';
import {
  initialAdminTableSort,
  nextAdminTableSort,
  sortApplicationsForDisplay,
  sortColumnIndicator,
} from '../../utils/adminTableSort';
import {
  getAnalysisHistoryTypeLabel,
  getAnalysisHistoryActionLabel,
  getHistoryScoreColumnLabel,
} from '../../constants/displayLabels';
import { INGRESSA_STORAGE_KEYS } from '../../utils/ingressaBrand';

const STORAGE_KEY = INGRESSA_STORAGE_KEYS.filtersPayment;

export const PaymentManager = React.memo(({ user }) => {
  const { selectedProcessId, setSelectedProcessId, processes } = useAdminProcess();
  const [payments, setPayments] = useState([]);
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterStatus ? String(p.filterStatus) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterCandidate, setFilterCandidate] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterCandidate ? String(p.filterCandidate) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterAnalyst, setFilterAnalyst] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) {
        const p = JSON.parse(s);
        const v = p && p.filterAnalyst ? String(p.filterAnalyst) : '';
        if (v === 'not_assigned') return '';
        return v;
      }
    } catch (e) {}
    return '';
  });
  const [filterCourse, setFilterCourse] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterCourse ? String(p.filterCourse) : ''; }
    } catch (e) {}
    return '';
  });
  const [filterCampus, setFilterCampus] = useState(() => {
    try {
      const s = sessionStorage.getItem(STORAGE_KEY);
      if (s) { const p = JSON.parse(s); return p && p.filterCampus ? String(p.filterCampus) : ''; }
    } catch (e) {}
    return '';
  });
  const { addNotification } = useNotifications();
  const [tableSort, setTableSort] = useState(() => initialAdminTableSort());

  useEffect(() => {
    try {
      const data = {
        filterStatus: String(filterStatus || ''),
        filterCandidate: String(filterCandidate || ''),
        filterAnalyst: String(filterAnalyst || ''),
        filterCourse: String(filterCourse || ''),
        filterCampus: String(filterCampus || '')
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {}
  }, [filterStatus, filterCandidate, filterAnalyst, filterCourse, filterCampus]);

  const clearFilters = useCallback(() => {
    setFilterStatus('');
    setFilterCandidate('');
    setFilterAnalyst('');
    setFilterCourse('');
    setFilterCampus('');
  }, []);
  
  // Estados para seleção de analista
  const [showAnalystModal, setShowAnalystModal] = useState(false);
  const [selectedApplicationForExemption, setSelectedApplicationForExemption] = useState(null);
  const [selectedAnalyst, setSelectedAnalyst] = useState('');
  const [analysts, setAnalysts] = useState([]);
  const [modalError, setModalError] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedApplicationToView, setSelectedApplicationToView] = useState(null);
  const [exemptionRequests, setExemptionRequests] = useState([]);
  const [rejectionReasons, setRejectionReasons] = useState([]);
  const [showExemptionEditModal, setShowExemptionEditModal] = useState(false);
  const [selectedApplicationForExemptionEdit, setSelectedApplicationForExemptionEdit] = useState(null);
  const [exemptionEditForm, setExemptionEditForm] = useState({ exemption_status: '', exemption_reason: '' });
  const [viewModalTab, setViewModalTab] = useState('details');
  const [analysisHistory, setAnalysisHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedForBulkExemption, setSelectedForBulkExemption] = useState(new Set());
  const [bulkExemptionAnalyst, setBulkExemptionAnalyst] = useState('');
  const [equitableExemptionAnalysts, setEquitableExemptionAnalysts] = useState(new Set());
  const [selectedForBulkPaid, setSelectedForBulkPaid] = useState(new Set());
  const [bulkPaidLoading, setBulkPaidLoading] = useState(false);

  const loadData = useCallback(async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const [paymentsData, processesData, applicationsData, usersData, analystsData, exemptionData, reasonsData] = await Promise.all([
        apiClient.getPayments(),
        apiClient.getProcesses(true),
        apiClient.getApplications(),
        apiClient.getUsers(),
        apiClient.getUsers('analyst'),
        apiClient.getExemptionRequests(),
        apiClient.getRejectionReasons({ context: 'exemption' })
      ]);

      const paymentsList = Array.isArray(paymentsData) ? paymentsData : [];
      const appsList = Array.isArray(applicationsData) ? applicationsData : [];
      const syncedApps = appsList.map(app => {
        const payment = paymentsList.find(p =>
          p.application_id === app.id || (p.user_id === app.user_id && p.process_id === app.process_id)
        );
        return payment ? { ...app, payment_status: payment.status } : app;
      });

      setPayments(paymentsList);
      setApplications(syncedApps);
      setUsers(Array.isArray(usersData) ? usersData : []);
      setAnalysts(Array.isArray(analystsData) ? analystsData : []);
      setExemptionRequests(Array.isArray(exemptionData) ? exemptionData : []);
      setRejectionReasons(Array.isArray(reasonsData) ? reasonsData : []);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      if (!isBackground) setError('Erro ao carregar dados');
    } finally {
      if (!isBackground) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setSelectedForBulkExemption(new Set());
    setBulkExemptionAnalyst('');
    setEquitableExemptionAnalysts(new Set());
    setSelectedForBulkPaid(new Set());
    setFilterAnalyst('');
    setTableSort(initialAdminTableSort());
  }, [selectedProcessId]);

  const hasModalOpen = showAnalystModal || showExemptionEditModal || showViewModal;

  const loadAnalysisHistory = useCallback(async (appId) => {
    if (!appId) return;
    setHistoryLoading(true);
    try {
      const data = await apiClient.getAnalysisHistory(appId);
      setAnalysisHistory(Array.isArray(data) ? data : []);
    } catch {
      setAnalysisHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (showViewModal && selectedApplicationToView && viewModalTab === 'history') {
      loadAnalysisHistory(selectedApplicationToView.id);
    }
  }, [showViewModal, selectedApplicationToView?.id, viewModalTab, loadAnalysisHistory]);

  useEffect(() => {
    if (hasModalOpen) return;
    const interval = setInterval(() => loadData(true), 60 * 1000);
    return () => clearInterval(interval);
  }, [loadData, hasModalOpen]);

  // Função para verificar e corrigir inconsistências nos dados
  const checkAndFixInconsistencies = useCallback(() => {
    console.log('Verificando inconsistências nos dados...');
    
    applications.forEach(app => {
      const payment = payments.find(p => 
        p.user_id === app.candidate_id && p.process_id === app.process_id
      );
      
      if (payment && app.payment_status !== payment.status) {
        console.warn(`Inconsistência encontrada para aplicação ${app.id} (${app.candidate_name}):`, {
          application_payment_status: app.payment_status,
          payment_status: payment.status
        });
      }
    });
  }, [applications, payments]);

  // Executar verificação apenas uma vez quando os dados são carregados
  useEffect(() => {
    if (applications.length > 0 && payments.length > 0) {
      // Usar setTimeout para evitar execução durante renderização
      const timer = setTimeout(() => {
        checkAndFixInconsistencies();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [applications.length, payments.length]); // Remover dependências que causam loop

  const getPaymentStatus = useCallback((userId, processId, applicationId = null) => {
    // Primeiro verificar o status da aplicação (mais confiável)
    if (applicationId) {
      const application = applications.find(app => app.id === applicationId);
      if (application) {
        // Pagamento confirmado pelo admin tem prioridade (sobrepõe status de isenção)
        if (application.payment_status === 'paid') return 'paid';
        // Verificar status de isenção
        if (application.exemption_status) {
          switch (application.exemption_status) {
            case 'approved':
              return 'exempted';
            case 'rejected':
              return 'exemption_rejected';
            case 'returned':
              return 'exemption_returned';
            case 'appealed':
              return 'exemption_appealed';
            case 'appeal_approved':
              return 'exemption_appeal_approved';
            case 'appeal_rejected':
              return 'exemption_appeal_rejected';
            case 'pending': {
              const hasExemptionAnalyst = application.exemption_analyst_id || application.exemption_analyst_name;
              if (!hasExemptionAnalyst) return 'pending_exemption_not_assigned';
              return application.exemption_request_status === 'analyzing'
                ? 'pending_exemption'
                : 'pending_exemption_assigned';
            }
            default:
              break;
          }
        }
        
        // Se não há status de isenção, verificar payment_status
        if (application.payment_status) {
          return application.payment_status;
        }
      }
    }
    
    // Se não tem applicationId, procurar a aplicação por user_id + process_id
    const application = applications.find(app => app.candidate_id === userId && app.process_id === processId);
    if (application) {
      if (application.payment_status === 'paid') return 'paid';
      if (application.exemption_status) {
        switch (application.exemption_status) {
          case 'approved':
            return 'exempted';
          case 'rejected':
            return 'exemption_rejected';
          case 'returned':
            return 'exemption_returned';
          case 'appealed':
            return 'exemption_appealed';
          case 'appeal_approved':
            return 'exemption_appeal_approved';
          case 'appeal_rejected':
            return 'exemption_appeal_rejected';
            case 'pending': {
              const hasExemptionAnalyst = application.exemption_analyst_id || application.exemption_analyst_name;
              if (!hasExemptionAnalyst) return 'pending_exemption_not_assigned';
              return application.exemption_request_status === 'analyzing'
                ? 'pending_exemption'
                : 'pending_exemption_assigned';
            }
          default:
              break;
        }
      }
      
      // Se não há status de isenção, verificar payment_status
      if (application.payment_status) {
        return application.payment_status;
      }
    }
    
    // Fallback: tentar encontrar por application_id na tabela payments
    if (applicationId) {
      const paymentByApp = payments.find(p => p.application_id === applicationId);
      if (paymentByApp) {
        return paymentByApp.status;
      }
    }
    
    // Fallback: tentar por user_id + process_id na tabela payments
    const payment = payments.find(p => p.user_id === userId && p.process_id === processId);
    if (payment) {
      return payment.status;
    }
    
    return 'pending';
  }, [payments, applications]);

  const getPaymentAmount = useCallback((userId, processId, applicationId = null) => {
    const payment = applicationId
      ? payments.find(p => p.application_id === applicationId)
      : payments.find(p => p.user_id === userId && p.process_id === processId);
    const process = processes.find(p => p.id === processId);
    
    if (payment) {
      return payment.amount;
    }
    
    // Se não há pagamento, usar o valor padrão do processo
    return process?.fee_amount || 50.00;
  }, [payments, processes]);

  const markAsPaid = useCallback(async (applicationId, skipReload = false) => {
    try {
      const application = applications.find(app => app.id === applicationId);
      
      if (!application) {
        console.error('Aplicação não encontrada:', applicationId);
        throw new Error('Inscrição não encontrada');
      }

      const process = processes.find(p => p.id === application.process_id);
      const feeAmount = process?.fee_amount || 50.00;

      // Verificar se já existe um pagamento para esta aplicação
      const existingPayment = payments.find(p =>
        p.application_id === applicationId ||
        (p.user_id === application.user_id && p.process_id === application.process_id)
      );

      if (existingPayment) {
        const payload = { status: 'paid', amount: feeAmount };
        if (existingPayment.application_id !== applicationId) {
          payload.application_id = applicationId;
        }
        const { error: updateError } = await apiClient.updatePayment(existingPayment.id, payload);
        if (updateError) throw new Error(updateError);
        setPayments(prev => prev.map(p =>
          p.id === existingPayment.id ? { ...p, status: 'paid', amount: feeAmount, application_id: applicationId } : p
        ));
      } else {
        const { data: created, error: createError } = await apiClient.createPayment({
          application_id: applicationId,
          status: 'paid',
          amount: feeAmount
        });
        if (createError) throw new Error(createError);
        setPayments(prev => [...prev, created]);
      }

      const updatedApplication = { ...application, payment_status: 'paid' };
      setApplications(prev => prev.map(app =>
        app.id === applicationId ? updatedApplication : app
      ));
      
      if (!skipReload) await loadData(true);
      if (!skipReload) addNotification('Sucesso', 'Pagamento marcado como realizado com sucesso!', 'success');
    } catch (err) {
      console.error('Erro em markAsPaid:', err);
      setError('Erro ao marcar pagamento');
      addNotification('Erro ao marcar pagamento', 'error');
    }
  }, [applications, payments, processes, addNotification, loadData]);

  const markAsPending = useCallback(async (applicationId) => {
    try {
      const application = applications.find(app => app.id === applicationId);
      
      if (!application) {
        console.error('Aplicação não encontrada:', applicationId);
        throw new Error('Inscrição não encontrada');
      }

      const process = processes.find(p => p.id === application.process_id);
      const feeAmount = process?.fee_amount || 50.00;

      // Verificar se já existe um pagamento para esta aplicação
      const existingPayment = payments.find(p =>
        p.application_id === applicationId ||
        (p.user_id === application.user_id && p.process_id === application.process_id)
      );

      if (existingPayment) {
        const payload = { status: 'pending', amount: feeAmount };
        if (existingPayment.application_id !== applicationId) {
          payload.application_id = applicationId;
        }
        const { error: updateError } = await apiClient.updatePayment(existingPayment.id, payload);
        if (updateError) throw new Error(updateError);
        setPayments(prev => prev.map(p =>
          p.id === existingPayment.id ? { ...p, status: 'pending', amount: feeAmount } : p
        ));
      } else {
        const { data: created, error: createError } = await apiClient.createPayment({
          application_id: applicationId,
          status: 'pending',
          amount: feeAmount
        });
        if (createError) throw new Error(createError);
        setPayments(prev => [...prev, created]);
      }

      const updatedApplication = { ...application, payment_status: 'pending' };
      setApplications(prev => prev.map(app =>
        app.id === applicationId ? updatedApplication : app
      ));
      
      await loadData(true);
      addNotification('Sucesso', 'Status alterado para pendente com sucesso!', 'success');
    } catch (err) {
      console.error('Erro em markAsPending:', err);
      setError('Erro ao alterar status');
      addNotification('Erro ao alterar status', 'error');
    }
  }, [applications, payments, processes, addNotification]);

  const assignExemptionForAnalysis = useCallback((applicationId) => {
    setModalError('');
    const application = applications.find(app => app.id === applicationId);
    if (!application) {
      setError('Aplicação não encontrada');
      return;
    }
    if (analysts.length === 0) {
      setModalError('Nenhum analista disponível. Cadastre analistas em Usuários.');
      setSelectedApplicationForExemption(application);
      setSelectedAnalyst('');
      setShowAnalystModal(true);
      return;
    }
    setSelectedApplicationForExemption(application);
    setSelectedAnalyst('');
    setShowAnalystModal(true);
  }, [applications, analysts]);

  const handleExemptionAssignment = useCallback(async () => {
    setModalError('');
    if (!selectedAnalyst || !selectedApplicationForExemption) {
      setModalError('Selecione um analista');
      return;
    }

    const analyst = analysts.find(a => a.id === parseInt(selectedAnalyst, 10));
    if (!analyst) {
      setModalError('Analista não encontrado');
      return;
    }

    try {
      // Verificar se já existe ExemptionRequest para esta inscrição
      const existingExemptions = await apiClient.getExemptionRequests();
      const hasExemption = Array.isArray(existingExemptions) &&
        existingExemptions.some(er => er.application_id === selectedApplicationForExemption.id);

      if (!hasExemption) {
        const { error: exError } = await apiClient.createExemptionRequest({
          application_id: selectedApplicationForExemption.id,
          documents: selectedApplicationForExemption.exemption_documents || []
        });
        if (exError) throw new Error(exError);
      }

      // Atribuir analista à inscrição (campo analyst_id na Application)
      const { error: appError } = await apiClient.updateApplication(selectedApplicationForExemption.id, {
        analyst_id: analyst.id,
        exemption_status: 'pending',
        assignment_type: 'exemption'
      });
      if (appError) throw new Error(appError);

      addNotification(
        '✅ Isenção Atribuída',
        `Pedido de isenção enviado para análise do analista ${analyst.name}`,
        'success'
      );

      setShowAnalystModal(false);
      setSelectedApplicationForExemption(null);
      setSelectedAnalyst('');
      setError('');
      await loadData();
    } catch (err) {
      console.error('Erro ao atribuir isenção:', err);
      setModalError(err.message || 'Erro ao atribuir isenção');
      addNotification('Erro ao atribuir isenção', err.message || 'Erro', 'error');
    }
  }, [selectedAnalyst, selectedApplicationForExemption, analysts, addNotification, loadData]);

  const handleViewApplication = useCallback((application) => {
    setSelectedApplicationToView(application);
    setViewModalTab('details');
    setShowViewModal(true);
  }, []);

  const handleEditExemption = useCallback((application) => {
    setSelectedApplicationForExemptionEdit(application);
    setExemptionEditForm({
      exemption_status: application.exemption_status || 'rejected',
      exemption_reason: application.exemption_reason || ''
    });
    setShowExemptionEditModal(true);
  }, []);

  const handleSaveExemptionEdit = useCallback(async () => {
    if (!selectedApplicationForExemptionEdit) return;
    if (!exemptionEditForm.exemption_status) {
      addNotification('Erro', 'Selecione a situação da isenção', 'error');
      return;
    }
    if (exemptionEditForm.exemption_status === 'rejected' && !exemptionEditForm.exemption_reason) {
      addNotification('Erro', 'Informe o motivo ao indeferir', 'error');
      return;
    }
    try {
      const { error } = await apiClient.updateApplication(selectedApplicationForExemptionEdit.id, {
        exemption_status: exemptionEditForm.exemption_status,
        exemption_reason: exemptionEditForm.exemption_reason || null
      });
      if (error) throw new Error(error);
      addNotification('Sucesso', 'Isenção atualizada', 'success');
      setShowExemptionEditModal(false);
      setSelectedApplicationForExemptionEdit(null);
      await loadData();
    } catch (err) {
      addNotification('Erro', err.message || 'Erro ao salvar', 'error');
    }
  }, [selectedApplicationForExemptionEdit, exemptionEditForm, addNotification, loadData]);

  const handleDownloadExemptionDocument = useCallback(async (exemptionRequest, index) => {
    const doc = (exemptionRequest?.documents || [])[index];
    const filename = doc?.name || `documento_${index + 1}.pdf`;
    try {
      const blob = await apiClient.downloadDocument({ exemptionRequestId: exemptionRequest.id, index });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      addNotification('Sucesso', `${filename} baixado`, 'success');
    } catch (e) {
      addNotification('Erro', e.message || `Não foi possível baixar ${filename}`, 'error');
    }
  }, [addNotification]);

  const filteredApplications = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    return applications.filter(app => {
      if (app.excluded_at) return false;
      const process = processes.find(p => p.id === app.process_id);
      const paymentStatus = getPaymentStatus(app.candidate_id, process?.id, app.id);

      const matchesProcess =
        procSel.empty || (!procSel.invalid && process?.id === procSel.id);
      const matchesStatus = !filterStatus || paymentStatus === filterStatus;
      const q = (filterCandidate || '').trim().toLowerCase();
      const matchesCandidate = !q || [app.candidate_name, app.candidate_cpf].some(v =>
        v && String(v).toLowerCase().includes(q)
      );
      const exemptionAnalystId = app.exemption_analyst_id;
      const matchesAnalyst =
        !filterAnalyst ||
        (exemptionAnalystId != null && String(exemptionAnalystId) === String(filterAnalyst));
      const matchesCourse = !filterCourse || String(app.course_name || '') === String(filterCourse);
      const matchesCampus = !filterCampus || String(app.course_campus || '') === String(filterCampus);
      
      return matchesProcess && matchesStatus && matchesCandidate && matchesAnalyst && matchesCourse && matchesCampus;
    });
  }, [applications, processes, payments, selectedProcessId, filterStatus, filterCandidate, filterAnalyst, filterCourse, filterCampus, getPaymentStatus]);

  const sortedApplications = useMemo(
    () => sortApplicationsForDisplay(filteredApplications, tableSort),
    [filteredApplications, tableSort]
  );

  const courseFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const names = new Set();
    applications.forEach((app) => {
      if (app.excluded_at) return;
      if (!procSel.empty && !procSel.invalid && app.process_id !== procSel.id) return;
      if (!procSel.empty && procSel.invalid) return;
      if (app.course_name) names.add(String(app.course_name));
    });
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, selectedProcessId]);

  const campusFilterOptions = useMemo(() => {
    const procSel = parseAdminSelectedProcessId(selectedProcessId);
    const campuses = new Set();
    applications.forEach((app) => {
      if (app.excluded_at) return;
      if (!procSel.empty && !procSel.invalid && app.process_id !== procSel.id) return;
      if (!procSel.empty && procSel.invalid) return;
      if (app.course_campus) campuses.add(String(app.course_campus));
    });
    return Array.from(campuses).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [applications, selectedProcessId]);

  const selectedProcess = useMemo(() => {
    const { id } = parseAdminSelectedProcessId(selectedProcessId);
    if (id == null) return null;
    return processes.find((p) => p.id === id) || null;
  }, [selectedProcessId, processes]);

  const historyScoreColumnLabel = getHistoryScoreColumnLabel(selectedProcess?.selection_mode);

  const analystsForProcess = useMemo(() => {
    const { empty, invalid, id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (empty) return analysts;
    if (invalid || pid == null) return [];
    return analysts.filter(a => {
      const pids = a.process_ids || [];
      return pids.length === 0 || pids.includes(pid);
    });
  }, [analysts, selectedProcessId]);

  /** Analistas de isenção no filtro: cadastrados no processo ∪ quem já analisou isenção em alguma inscrição deste processo. */
  const exemptionAnalystFilterOptions = useMemo(() => {
    const { id: pid } = parseAdminSelectedProcessId(selectedProcessId);
    if (pid == null) return [];
    const byId = new Map();
    analystsForProcess.forEach((a) => byId.set(String(a.id), { id: a.id, name: a.name || `Usuário #${a.id}` }));
    applications.forEach((app) => {
      if (app.excluded_at || Number(app.process_id) !== pid || !app.exemption_analyst_id) return;
      const id = app.exemption_analyst_id;
      const key = String(id);
      if (byId.has(key)) return;
      let name = app.exemption_analyst_name;
      if (!name) {
        const u = users.find((u) => Number(u.id) === Number(id));
        if (u) name = u.role === 'admin' ? (u.name || 'Administrador') : (u.name || `Usuário #${id}`);
        else name = `Usuário #${id}`;
      }
      byId.set(key, { id, name });
    });
    return Array.from(byId.values()).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR'));
  }, [selectedProcessId, analystsForProcess, applications, users]);

  useEffect(() => {
    if (!filterAnalyst || !selectedProcessId) return;
    const ok = exemptionAnalystFilterOptions.some((o) => String(o.id) === String(filterAnalyst));
    if (!ok) setFilterAnalyst('');
  }, [filterAnalyst, selectedProcessId, exemptionAnalystFilterOptions]);

  const exemptionAnalystCellLabel = useCallback((app) => {
    if (app.exemption_analyst_name) return app.exemption_analyst_name;
    if (!app.exemption_analyst_id) return '—';
    const u = users.find((u) => Number(u.id) === Number(app.exemption_analyst_id));
    if (u) return u.role === 'admin' ? (u.name || 'Administrador') : (u.name || `Usuário #${u.id}`);
    return `Usuário #${app.exemption_analyst_id}`;
  }, [users]);

  const pendingExemptionForBulk = useMemo(() => {
    return filteredApplications.filter(app => {
      const process = processes.find(p => p.id === app.process_id);
      const ps = getPaymentStatus(app.candidate_id, process?.id, app.id);
      return ps === 'pending_exemption_not_assigned';
    });
  }, [filteredApplications, processes, getPaymentStatus]);

  const handleBulkAssignExemption = useCallback(async () => {
    if (!bulkExemptionAnalyst || selectedForBulkExemption.size === 0) {
      addNotification('Selecione um analista e ao menos uma inscrição', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulkExemption);
    try {
      const { data, error } = await apiClient.bulkAssignExemption(ids, parseInt(bulkExemptionAnalyst, 10));
      if (error) throw new Error(error);
      const count = data?.assigned_count ?? 0;
      const analyst = analystsForProcess.find(a => a.id === parseInt(bulkExemptionAnalyst, 10));
      addNotification(`Atribuição em massa`, `${count} isenção(ões) atribuída(s) a ${analyst?.name || 'analista'}`, 'success');
      setSelectedForBulkExemption(new Set());
      setBulkExemptionAnalyst('');
      await loadData(true);
    } catch (err) {
      addNotification('Erro ao atribuir', err.message || 'Erro', 'error');
    }
  }, [bulkExemptionAnalyst, selectedForBulkExemption, analystsForProcess, addNotification, loadData]);

  const handleBulkAssignExemptionEquitable = useCallback(async () => {
    if (selectedForBulkExemption.size === 0 || equitableExemptionAnalysts.size < 2) {
      addNotification('Atenção', 'Selecione inscrições e marque ao menos 2 analistas para distribuição equânime.', 'warning');
      return;
    }
    const ids = Array.from(selectedForBulkExemption);
    const analystIds = Array.from(equitableExemptionAnalysts)
      .map((x) => parseInt(x, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    try {
      const { data, error } = await apiClient.bulkAssignExemptionEquitable(ids, analystIds);
      if (error) throw new Error(error);
      const count = data?.assigned_count ?? 0;
      const per = data?.per_analyst || {};
      const parts = Object.entries(per)
        .filter(([, c]) => c > 0)
        .map(([id, c]) => {
          const an = analystsForProcess.find((x) => String(x.id) === String(id));
          return `${an?.name || id}: ${c}`;
        });
      addNotification(
        'Distribuição equânime',
        `${count} isenção(ões) atribuída(s)${parts.length ? ` — ${parts.join('; ')}` : ''}`,
        'success'
      );
      setSelectedForBulkExemption(new Set());
      setEquitableExemptionAnalysts(new Set());
      await loadData(true);
    } catch (err) {
      addNotification('Erro ao distribuir', err.message || 'Erro', 'error');
    }
  }, [selectedForBulkExemption, equitableExemptionAnalysts, analystsForProcess, addNotification, loadData]);

  // Apenas pendente sem isenção ou isenção rejeitada: candidato pode pagar via GRU. Não incluir isenção em análise.
  const CAN_MARK_PAID = ['pending', 'exemption_rejected', 'exemption_appeal_rejected'];
  const pendingPaidForBulk = useMemo(() => {
    return filteredApplications.filter(app => {
      const process = processes.find(p => p.id === app.process_id);
      const ps = getPaymentStatus(app.candidate_id, process?.id, app.id);
      return CAN_MARK_PAID.includes(ps);
    });
  }, [filteredApplications, processes, getPaymentStatus]);

  const handleBulkMarkAsPaid = useCallback(async () => {
    if (selectedForBulkPaid.size === 0) {
      addNotification('Selecione ao menos uma inscrição para confirmar pagamento', 'warning');
      return;
    }
    setBulkPaidLoading(true);
    const ids = Array.from(selectedForBulkPaid);
    let ok = 0;
    for (const appId of ids) {
      try {
        await markAsPaid(appId, true);
        ok++;
      } catch {
        addNotification('Aviso', `Falha ao confirmar inscrição #${appId}`, 'warning');
      }
    }
    setSelectedForBulkPaid(new Set());
    setBulkPaidLoading(false);
    if (ok > 0) {
      await loadData(true);
      addNotification('Sucesso', `${ok} pagamento(s) confirmado(s)`, 'success');
    }
  }, [selectedForBulkPaid, markAsPaid, addNotification, loadData]);

  const exportPaymentsData = useCallback(() => {
    const data = sortedApplications.map(app => {
      const process = processes.find(p => p.id === app.process_id);
      const payment = payments.find(p => p.application_id === app.id) ||
        payments.find(p => p.user_id === (app.user_id || app.candidate_id) && p.process_id === process?.id);
      
      return {
        'Nº Inscrição': app.id,
        'Nome do Candidato': app.candidate_name || 'N/A',
        'CPF': app.candidate_cpf || 'N/A',
        'Curso': app.course_name || 'N/A',
        'Unidade': app.course_campus || '—',
        'Status do Pagamento': payment?.status === 'paid' ? 'PAGO' : payment?.status === 'exempt' ? 'ISENTO' : 'PENDENTE',
        'Valor': formatCurrency(payment?.amount || 0),
        'Data do Pagamento': payment?.payment_date ? formatDate(payment.payment_date) : 'N/A'
      };
    });
    
    exportToCSV(data, 'pagamentos_ingressa.csv');
    addNotification('Relatório exportado com sucesso!', 'success');
  }, [sortedApplications, processes, payments, addNotification]);

  if (loading && !(showAnalystModal || showExemptionEditModal || showViewModal)) {
    return <LoadingSpinner text="Carregando dados de pagamentos..." />;
  }

  // Funções de status agora importadas do arquivo padronizado

  return (
    <div style={styles.contentContainer}>
      <div style={{...styles.flexBetween, ...styles.mb6}}>
        <div>
          <h2 style={{ 
            fontSize: '32px', 
            fontWeight: 'bold', 
            color: '#111827', 
            marginBottom: '8px' 
          }}>
            💰 Gerenciar Pagamentos
          </h2>
          <p style={{ fontSize: '16px', color: '#6B7280' }}>
            Controle de pagamentos e isenções de processos seletivos
          </p>
        </div>
        <div style={{...styles.flex, ...styles.gap4}}>
          <button
            onClick={exportPaymentsData}
            style={{...styles.button, ...styles.buttonInfo}}
          >
            📄 Exportar Relatório
          </button>
        </div>
      </div>

      <div style={{ ...styles.card, marginBottom: '24px' }}>
        <label style={styles.label}>Processo seletivo:</label>
        <select
          value={selectedProcessId}
          onChange={setSelectedProcessId}
          style={{ ...styles.select, maxWidth: '400px' }}
        >
          <option value="">Selecione o processo</option>
          {processes.map((p) => (
            <option key={p.id} value={String(p.id)}>{p.name}</option>
          ))}
        </select>
      </div>

      <div style={{...styles.card, ...styles.mb6}}>
        <div style={{ ...styles.flexBetween, marginBottom: '16px' }}>
          <h3 style={{ ...styles.cardTitle, margin: 0 }}>Filtros</h3>
          <button
            onClick={clearFilters}
            style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 12px', fontSize: '13px', flexShrink: 0 }}
            title="Limpar todos os filtros"
          >
            🗑️ Limpar filtros
          </button>
        </div>
        <div style={{...styles.flex, ...styles.gap4, ...styles.flexWrap, alignItems: 'flex-end'}}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Candidato:</label>
            <input
              type="text"
              value={filterCandidate}
              onChange={(e) => setFilterCandidate(e.target.value)}
              placeholder="Nome, CPF..."
              style={{ ...styles.select, fontFamily: 'inherit' }}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Analista isenção:</label>
            <select
              value={filterAnalyst}
              onChange={(e) => setFilterAnalyst(e.target.value)}
              style={{
                ...styles.select,
                opacity: selectedProcessId ? 1 : 0.65,
              }}
              disabled={!selectedProcessId}
              title={
                selectedProcessId
                  ? 'Analistas ligados ao processo ou que já analisaram isenção neste processo (inclui admin)'
                  : 'Selecione o processo seletivo para habilitar este filtro'
              }
            >
              <option value="">{selectedProcessId ? 'Todos' : 'Selecione o processo…'}</option>
              {exemptionAnalystFilterOptions.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Situação:</label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.select}
            >
              <option value="">Todas as situações</option>
              <option value="pending">Pendente</option>
              <option value="pending_exemption_not_assigned">Isenção Não Atribuída</option>
              <option value="pending_exemption_assigned">Isenção Atribuída</option>
              <option value="pending_exemption">Isenção em Análise</option>
              <option value="exemption_rejected">Isenção Rejeitada</option>
              <option value="exemption_returned">Isenção Devolvida</option>
              <option value="exemption_appealed">Recurso de Isenção em Análise</option>
              <option value="exemption_appeal_approved">Recurso de Isenção Aprovado</option>
              <option value="exemption_appeal_rejected">Recurso de Isenção Rejeitado</option>
              <option value="paid">Pago</option>
              <option value="exempted">Isento</option>
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Curso:</label>
            <select value={filterCourse} onChange={(e) => setFilterCourse(e.target.value)} style={styles.select}>
              <option value="">Todos os cursos</option>
              {courseFilterOptions.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.label}>Unidade:</label>
            <select value={filterCampus} onChange={(e) => setFilterCampus(e.target.value)} style={styles.select}>
              <option value="">Todas as unidades</option>
              {campusFilterOptions.map((campus) => (
                <option key={campus} value={campus}>{campus}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={{ marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: '15px', lineHeight: 1.2, fontWeight: '600', color: '#111827' }}>
            Inscrições ({filteredApplications.length})
          </h3>
        </div>
        <div style={{ ...styles.mb4 }}>
          {pendingPaidForBulk.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
              <button
                type="button"
                onClick={() => setSelectedForBulkPaid(new Set(pendingPaidForBulk.map(a => a.id)))}
                style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
              >
                Selecionar todos ({pendingPaidForBulk.length})
              </button>
              <button
                type="button"
                onClick={() => setSelectedForBulkPaid(new Set())}
                style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
              >
                Desmarcar
              </button>
              <button
                type="button"
                onClick={handleBulkMarkAsPaid}
                disabled={selectedForBulkPaid.size === 0 || bulkPaidLoading}
                style={{
                  ...styles.button,
                  backgroundColor: '#10B981',
                  color: 'white',
                  padding: '6px 12px',
                  fontSize: 12,
                  opacity: selectedForBulkPaid.size === 0 || bulkPaidLoading ? 0.6 : 1
                }}
              >
                {bulkPaidLoading ? 'Confirmando...' : `✅ Confirmar pagamentos (${selectedForBulkPaid.size})`}
              </button>
            </div>
          )}
          {pendingExemptionForBulk.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setSelectedForBulkExemption(new Set(pendingExemptionForBulk.map(a => a.id)))}
                style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
              >
                Selecionar todos ({pendingExemptionForBulk.length})
              </button>
              <button
                type="button"
                onClick={() => setSelectedForBulkExemption(new Set())}
                style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 12 }}
              >
                Desmarcar
              </button>
              <select
                value={bulkExemptionAnalyst}
                onChange={(e) => setBulkExemptionAnalyst(e.target.value)}
                style={{ ...styles.select, padding: '6px 10px', minWidth: 180 }}
                placeholder="Analista"
              >
                <option value="">Atribuir a...</option>
                {analystsForProcess.map(a => (
                  <option key={a.id} value={String(a.id)}>{a.name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleBulkAssignExemption}
                disabled={!bulkExemptionAnalyst || selectedForBulkExemption.size === 0}
                style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12 }}
              >
                Atribuir selecionados ({selectedForBulkExemption.size})
              </button>
              <div style={{ flex: '1 1 100%', width: '100%', marginTop: 10, paddingTop: 10, borderTop: '1px solid #E5E7EB' }}>
                <div style={{ fontSize: 12, color: '#374151', fontWeight: 600, marginBottom: 4 }}>Distribuição equânime (rodízio)</div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
                  Marque 2 ou mais analistas. As inscrições marcadas na coluna ☐ serão distribuídas de forma alternada entre eles.
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 14px', marginBottom: 8 }}>
                  {analystsForProcess.map((a) => (
                    <label key={a.id} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={equitableExemptionAnalysts.has(String(a.id))}
                        onChange={(e) => {
                          setEquitableExemptionAnalysts((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(String(a.id));
                            else next.delete(String(a.id));
                            return next;
                          });
                        }}
                      />
                      {a.name}
                    </label>
                  ))}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => setEquitableExemptionAnalysts(new Set(analystsForProcess.map((x) => String(x.id))))}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}
                  >
                    Marcar todos os analistas do processo
                  </button>
                  <button
                    type="button"
                    onClick={() => setEquitableExemptionAnalysts(new Set())}
                    style={{ ...styles.button, ...styles.buttonSecondary, padding: '6px 10px', fontSize: 11 }}
                  >
                    Limpar marcação
                  </button>
                  <button
                    type="button"
                    onClick={handleBulkAssignExemptionEquitable}
                    disabled={selectedForBulkExemption.size === 0 || equitableExemptionAnalysts.size < 2}
                    style={{ ...styles.button, ...styles.buttonPrimary, padding: '6px 12px', fontSize: 12, opacity: (selectedForBulkExemption.size === 0 || equitableExemptionAnalysts.size < 2) ? 0.55 : 1 }}
                  >
                    Distribuir ({selectedForBulkExemption.size} inscrições)
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <Alert type="error" message={error} onClose={() => setError('')} />
        )}

        {filteredApplications.length === 0 ? (
          <div style={{...styles.textCenter, ...styles.py8}}>
            <p style={{ color: '#6B7280', fontSize: '16px' }}>
              Nenhuma inscrição encontrada com os filtros aplicados.
            </p>
          </div>
        ) : (
          <div style={styles.tableContainer}>
            <table style={{ ...styles.table, width: '100%', fontSize: '10px', hyphens: 'none' }}>
              <thead>
                <tr>
                  {(pendingExemptionForBulk.length > 0 || pendingPaidForBulk.length > 0) && (
                    <th style={{ ...styles.tableHeaderCell, fontSize: '9px', padding: '8px 4px', width: '36px' }} title="Selecionar">☐</th>
                  )}
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'id' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableHeaderCell, fontSize: '9px', padding: '8px 6px', width: '45px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((p) => nextAdminTableSort(p, 'id'))}
                    title="Ordenar por número da inscrição"
                  >
                    Nº{sortColumnIndicator(tableSort, 'id')}
                  </th>
                  <th
                    role="columnheader"
                    aria-sort={tableSort.key === 'candidate' ? (tableSort.dir === 'desc' ? 'descending' : 'ascending') : 'none'}
                    style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setTableSort((p) => nextAdminTableSort(p, 'candidate'))}
                    title="Ordenar por nome do candidato"
                  >
                    Candidato{sortColumnIndicator(tableSort, 'candidate')}
                  </th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Curso</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Unidade</th>
                  <th style={{ ...styles.tableHeaderCell, minWidth: '70px', fontSize: '9px', padding: '8px 8px' }}>Situação</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Valor</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '9px', padding: '8px 6px' }}>Ref. GRU</th>
                  <th style={{ ...styles.tableHeaderCell, fontSize: '10px', padding: '8px 10px' }}>Analista isenção</th>
                  <th style={{ ...styles.tableHeaderCell, minWidth: '100px', fontSize: '10px', padding: '8px 10px', textAlign: 'center' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {sortedApplications.map(application => {
                  const process = processes.find(p => p.id === application.process_id);
                  const user = users.find(u => u.id === application.candidate_id);
                  const paymentStatus = getPaymentStatus(application.candidate_id, process?.id, application.id);
                  const paymentAmount = getPaymentAmount(application.user_id || application.candidate_id, process?.id, application.id);
                  const payment = payments.find(p => p.application_id === application.id) || payments.find(p => p.user_id === (application.user_id || application.candidate_id) && p.process_id === application.process_id);
                  
                  const isPendingExemption = paymentStatus === 'pending_exemption_not_assigned';
                  const canMarkPaid = CAN_MARK_PAID.includes(paymentStatus);
                  return (
                    <tr key={application.id} style={styles.tableRow}>
                      {(pendingExemptionForBulk.length > 0 || pendingPaidForBulk.length > 0) && (
                        <td style={{ ...styles.tableCell, padding: '6px 4px', width: '36px', textAlign: 'center' }}>
                          {isPendingExemption ? (
                            <input
                              type="checkbox"
                              checked={selectedForBulkExemption.has(application.id)}
                              onChange={(e) => {
                                setSelectedForBulkExemption(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(application.id);
                                  else next.delete(application.id);
                                  return next;
                                });
                              }}
                            />
                          ) : canMarkPaid ? (
                            <input
                              type="checkbox"
                              checked={selectedForBulkPaid.has(application.id)}
                              onChange={(e) => {
                                setSelectedForBulkPaid(prev => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(application.id);
                                  else next.delete(application.id);
                                  return next;
                                });
                              }}
                            />
                          ) : null}
                        </td>
                      )}
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 6px', fontWeight: '600', color: '#374151' }}>
                        #{application.id}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                            {user?.name || application.candidate_name || 'N/A'}
                          </div>
                          <div style={{ fontSize: '9px', color: '#6B7280' }}>
                            CPF: {application.candidate_cpf || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <div>
                          <div style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                            {application.course_name || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>{application.course_campus || '—'}</td>
                      <td style={{ ...styles.tableCell, verticalAlign: 'middle', minWidth: '70px', whiteSpace: 'normal', padding: '8px 8px' }}>
                        <span style={{ ...getStatusBadgeStyle(paymentStatus), ...styles.tableCellBadge, fontSize: '9px', padding: '4px 8px' }}>
                          {getPaymentStatusLabel(paymentStatus)}
                        </span>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        <span style={{ fontWeight: '600', color: '#111827', fontSize: '10px' }}>
                          {paymentStatus === 'paid' ? formatCurrency(paymentAmount) : formatCurrency(0)}
                        </span>
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '9px', padding: '8px 6px', color: '#6B7280' }}>
                        {payment?.gru_referencia || '—'}
                      </td>
                      <td style={{ ...styles.tableCell, fontSize: '10px', padding: '8px 10px' }}>
                        {exemptionAnalystCellLabel(application)}
                      </td>
                      <td style={{ ...styles.tableCell, minWidth: '100px', textAlign: 'center', whiteSpace: 'normal', padding: '8px 10px' }}>
                        {paymentStatus === 'pending' && (
                          <button
                            onClick={() => markAsPaid(application.id)}
                            style={{
                              padding: '3px 6px',
                              border: 'none',
                              borderRadius: '6px',
                              backgroundColor: '#10B981',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '9px',
                              fontWeight: '500'
                            }}
                            title="Confirmar pagamento (valor do processo)"
                          >
                            ✅ Pago
                          </button>
                        )}
                        {paymentStatus === 'pending_exemption_not_assigned' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            <button
                              onClick={() => assignExemptionForAnalysis(application.id)}
                              style={{ ...getButtonStyle('assignment', 'small'), padding: '3px 5px', fontSize: '9px' }}
                            >
                              {ACTION_ICONS.assign} Atribuir
                            </button>
                            <button
                              onClick={() => handleEditExemption(application)}
                              title="Analisar isenção"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '9px',
                                fontWeight: '500'
                              }}
                            >
                              {ACTION_ICONS.analyze}
                            </button>
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                          </div>
                        )}
                        {(paymentStatus === 'pending_exemption_assigned' || paymentStatus === 'pending_exemption') && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            {!application.exemption_analyst_id && !application.exemption_analyst_name && (
                              <button
                                onClick={() => handleEditExemption(application)}
                                title="Analisar isenção"
                                style={{
                                  padding: '3px 6px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  backgroundColor: '#3B82F6',
                                  color: 'white',
                                  cursor: 'pointer',
                                  fontSize: '9px',
                                  fontWeight: '500'
                                }}
                              >
                                {ACTION_ICONS.analyze}
                              </button>
                            )}
                            {paymentStatus === 'pending_exemption_assigned' && (
                              <button
                                onClick={() => assignExemptionForAnalysis(application.id)}
                                title="Alterar atribuição"
                                style={{
                                  padding: '3px 6px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  backgroundColor: '#E0E7FF',
                                  color: '#4338CA',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                              >
                                🔄
                              </button>
                            )}
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'exemption_rejected' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                            {user?.id && Number(application.exemption_analyst_id) === Number(user.id) && (
                              <button
                                onClick={() => handleEditExemption(application)}
                                title="Editar análise de isenção"
                                style={{
                                  padding: '3px 6px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  backgroundColor: '#E0E7FF',
                                  color: '#4338CA',
                                  cursor: 'pointer',
                                  fontSize: '9px',
                                  fontWeight: '500'
                                }}
                              >
                                ✏️ Editar
                              </button>
                            )}
                            <button
                              onClick={() => markAsPaid(application.id)}
                              style={{
                                padding: '4px 8px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#10B981',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '9px',
                                fontWeight: '500'
                              }}
                              title="Confirmar pagamento (valor do processo)"
                            >
                              ✅ Pago
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'exemption_returned' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            <button
                              onClick={() => handleEditExemption(application)}
                              title="Analisar isenção"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '9px',
                                fontWeight: '500'
                              }}
                            >
                              {ACTION_ICONS.analyze}
                            </button>
                            <button
                              onClick={() => assignExemptionForAnalysis(application.id)}
                              title="Atribuir"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#E0E7FF',
                                color: '#4338CA',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.assign}
                            </button>
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'exemption_appealed' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            {(!application.exemption_analyst_id && !application.exemption_analyst_name) && (
                            <button
                              onClick={() => handleEditExemption(application)}
                              title="Analisar isenção"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '9px',
                                fontWeight: '500'
                              }}
                            >
                              {ACTION_ICONS.analyze}
                            </button>
                            )}
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                            <button
                              onClick={() => markAsPaid(application.id)}
                              style={{
                                padding: '4px 8px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#10B981',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '9px',
                                fontWeight: '500'
                              }}
                              title="Confirmar pagamento (valor do processo)"
                            >
                              ✅ Pago
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'exemption_appeal_approved' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center'}}>
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'exemption_appeal_rejected' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center', flexWrap: 'wrap'}}>
                            {(!application.exemption_analyst_id && !application.exemption_analyst_name) && (
                            <button
                              onClick={() => handleEditExemption(application)}
                              title="Analisar isenção"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#3B82F6',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: '9px',
                                fontWeight: '500'
                              }}
                            >
                              {ACTION_ICONS.analyze}
                            </button>
                            )}
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'paid' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center'}}>
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                            <button
                              onClick={() => markAsPending(application.id)}
                              title="Reverter para pendente"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#FEF3C7',
                                color: '#92400E',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              ↩️
                            </button>
                          </div>
                        )}
                        {paymentStatus === 'exempted' && (
                          <div style={{...styles.flex, ...styles.gap2, justifyContent: 'center'}}>
                            <button
                              onClick={() => handleViewApplication(application)}
                              title="Ver detalhes"
                              style={{
                                padding: '3px 6px',
                                border: 'none',
                                borderRadius: '6px',
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                            >
                              {ACTION_ICONS.view}
                            </button>
                            {user?.id && Number(application.exemption_analyst_id) === Number(user.id) && (
                              <button
                                onClick={() => handleEditExemption(application)}
                                title="Editar análise de isenção"
                                style={{
                                  padding: '3px 6px',
                                  border: 'none',
                                  borderRadius: '6px',
                                  backgroundColor: '#E0E7FF',
                                  color: '#4338CA',
                                  cursor: 'pointer',
                                  fontSize: '9px',
                                  fontWeight: '500'
                                }}
                              >
                                ✏️ Editar
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Seleção de Analista para Isenção */}
      {showAnalystModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '500px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '18px',
              fontWeight: '600',
              color: '#1F2937'
            }}>
              🎫 Atribuir Isenção para Análise
            </h3>

            {modalError && (
              <div style={{ marginBottom: '16px' }}>
                <Alert type="error" message={modalError} onClose={() => setModalError('')} />
              </div>
            )}
            
            <div style={{ marginBottom: '16px' }}>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
                <strong>Candidato:</strong> {selectedApplicationForExemption?.candidate_name}
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#6B7280' }}>
                <strong>CPF:</strong> {selectedApplicationForExemption?.candidate_cpf}
              </p>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6B7280' }}>
                <strong>Curso:</strong> {selectedApplicationForExemption?.course_name}<br/>
                <strong>Unidade:</strong> {selectedApplicationForExemption?.course_campus || '—'}
              </p>
              {(selectedApplicationForExemption?.exemption_return_reason || selectedApplicationForExemption?.exemption_returned_by_name) && (
                <div style={{
                  marginTop: '12px',
                  padding: '12px',
                  backgroundColor: '#FEF3C7',
                  borderRadius: '6px',
                  border: '1px solid #F59E0B',
                  fontSize: '13px',
                  color: '#92400E'
                }}>
                  <strong>↩️ Última devolução:</strong>
                  {selectedApplicationForExemption.exemption_returned_by_name && (
                    <p style={{ margin: '4px 0 0 0' }}>
                      <strong>Devolvido por:</strong> {selectedApplicationForExemption.exemption_returned_by_name}
                      {selectedApplicationForExemption.exemption_returned_at && (
                        <span> em {formatDate(selectedApplicationForExemption.exemption_returned_at)}</span>
                      )}
                    </p>
                  )}
                  {selectedApplicationForExemption.exemption_return_reason && (
                    <p style={{ margin: '8px 0 0 0', whiteSpace: 'pre-wrap' }}>
                      <strong>Motivo:</strong> {selectedApplicationForExemption.exemption_return_reason}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151'
              }}>
                Selecione o Analista:
              </label>
              <select
                value={selectedAnalyst}
                onChange={(e) => setSelectedAnalyst(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Escolha um analista...</option>
                {analystsForProcess.map(analyst => (
                  <option key={analyst.id} value={analyst.id}>
                    {analyst.name} ({analyst.email})
                  </option>
                ))}
              </select>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => {
                  setShowAnalystModal(false);
                  setSelectedApplicationForExemption(null);
                  setSelectedAnalyst('');
                  setModalError('');
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleExemptionAssignment}
                disabled={!selectedAnalyst}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: selectedAnalyst ? '#3B82F6' : '#9CA3AF',
                  color: 'white',
                  fontSize: '14px',
                  cursor: selectedAnalyst ? 'pointer' : 'not-allowed'
                }}
              >
                Atribuir para Análise
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Editar Isenção - Analisar/Editar status e motivo */}
      {showExemptionEditModal && selectedApplicationForExemptionEdit && (() => {
        const exReq = exemptionRequests.find(er => er.application_id === selectedApplicationForExemptionEdit.id);
        const exDocs = exReq?.documents || [];
        return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            minWidth: '400px',
            maxWidth: '500px',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>
              🔍 Analisar/Editar Isenção
            </h3>
            <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6B7280' }}>
              {selectedApplicationForExemptionEdit.candidate_name} – {processes.find(p => p.id === selectedApplicationForExemptionEdit.process_id)?.name || 'N/A'}
            </p>
            {exDocs.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#F9FAFB', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Documentos da isenção</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {exDocs.map((doc, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', backgroundColor: 'white', borderRadius: '4px', border: '1px solid #E5E7EB' }}>
                      <span style={{ fontSize: '13px' }}>📄 {doc.name || `Documento ${idx + 1}`}</span>
                      <button
                        onClick={() => exReq && handleDownloadExemptionDocument(exReq, idx)}
                        style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 10px', fontSize: '12px' }}
                      >
                        Baixar
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Situação da isenção</label>
              <select
                value={exemptionEditForm.exemption_status}
                onChange={(e) => setExemptionEditForm(prev => ({ ...prev, exemption_status: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  fontSize: '14px'
                }}
              >
                <option value="approved">✅ Aprovado</option>
                <option value="rejected">❌ Indeferido</option>
                <option value="pending">⏳ Pendente</option>
              </select>
            </div>
            {exemptionEditForm.exemption_status === 'rejected' && (
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Motivo do indeferimento</label>
                <select
                  value={exemptionEditForm.exemption_reason}
                  onChange={(e) => setExemptionEditForm(prev => ({ ...prev, exemption_reason: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    border: '1px solid #D1D5DB',
                    fontSize: '14px'
                  }}
                >
                  <option value="">Selecione o motivo</option>
                  {rejectionReasons.map(r => (
                    <option key={r.id} value={String(r.id)}>{r.title || r.description || `Motivo ${r.id}`}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' }}>
              <button
                onClick={() => { setShowExemptionEditModal(false); setSelectedApplicationForExemptionEdit(null); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveExemptionEdit}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  fontSize: '14px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Modal Ver Inscrição - acesso completo à isenção */}
      {showViewModal && selectedApplicationToView && (() => {
        const exReq = exemptionRequests.find(er => er.application_id === selectedApplicationToView.id) ||
          (selectedApplicationToView.exemption_request_id && exemptionRequests.find(er => er.id === selectedApplicationToView.exemption_request_id));
        const exDocs = exReq?.documents || [];
        return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '24px',
            borderRadius: '8px',
            minWidth: '450px',
            maxWidth: '600px',
            maxHeight: '85vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#1F2937' }}>
              {ACTION_ICONS.view} Detalhes da Inscrição e Isenção
            </h3>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #E5E7EB' }}>
              <button
                onClick={() => setViewModalTab('details')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: viewModalTab === 'details' ? '#3B82F6' : 'transparent',
                  color: viewModalTab === 'details' ? 'white' : '#6B7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Detalhes
              </button>
              <button
                onClick={() => setViewModalTab('history')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  background: viewModalTab === 'history' ? '#3B82F6' : 'transparent',
                  color: viewModalTab === 'history' ? 'white' : '#6B7280',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Histórico
              </button>
            </div>
            {viewModalTab === 'details' && (
            <>
            <div style={{ fontSize: '14px', color: '#374151', lineHeight: 1.6, marginBottom: '20px' }}>
              <p><strong>Inscrição Nº:</strong> #{selectedApplicationToView.id}</p>
              <p><strong>Candidato:</strong> {selectedApplicationToView.candidate_name || 'N/A'}</p>
              <p><strong>CPF:</strong> {selectedApplicationToView.candidate_cpf || 'N/A'}</p>
              <p><strong>Processo:</strong> {processes.find(p => p.id === selectedApplicationToView.process_id)?.name || 'N/A'}</p>
              <p><strong>Curso:</strong> {selectedApplicationToView.course_name || 'N/A'}</p>
              <p><strong>Unidade:</strong> {selectedApplicationToView.course_campus || '—'}</p>
              <p><strong>Analista Isenção:</strong>{' '}
                {(() => {
                  const l = exemptionAnalystCellLabel(selectedApplicationToView);
                  return l === '—' ? 'Não atribuído' : l;
                })()}
              </p>
              <p><strong>Situação Pagamento:</strong> {getPaymentStatusLabel(getPaymentStatus(selectedApplicationToView.candidate_id, selectedApplicationToView.process_id, selectedApplicationToView.id))}</p>
            </div>

            {(selectedApplicationToView.exemption_status && selectedApplicationToView.exemption_status !== 'not_requested') && (
              <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#FEF3C7', borderRadius: '8px', border: '1px solid #F59E0B' }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#92400E' }}>🎫 Pedido de Isenção</h4>
                <p style={{ margin: '0 0 8px 0', fontSize: '13px' }}>
                  <strong>Status:</strong> {selectedApplicationToView.exemption_status === 'approved' ? '✅ Aprovado' : selectedApplicationToView.exemption_status === 'rejected' ? '❌ Indeferido' : selectedApplicationToView.exemption_status}
                </p>
                {selectedApplicationToView.exemption_reason && (
                  <p style={{ margin: '8px 0 0 0', fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                    <strong>Motivo indeferimento:</strong> {selectedApplicationToView.exemption_reason}
                  </p>
                )}
                <div style={{ marginTop: '12px' }}>
                  <strong style={{ fontSize: '13px' }}>Documentos:</strong>
                  {exDocs.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                      {exDocs.map((doc, idx) => (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
                          <span style={{ fontSize: '13px' }}>📄 {doc.name || `Documento ${idx + 1}`}</span>
                          <button
                            onClick={() => handleDownloadExemptionDocument(exReq, idx)}
                            style={{ ...styles.button, ...styles.buttonSecondary, padding: '4px 10px', fontSize: '12px' }}
                          >
                            Baixar
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6B7280', fontStyle: 'italic' }}>Nenhum documento anexado</p>
                  )}
                </div>
              </div>
            )}
            </>
            )}
            {viewModalTab === 'history' && (
              <div style={{ marginBottom: '20px' }}>
                {historyLoading ? (
                  <p style={{ fontSize: '14px', color: '#6B7280' }}>Carregando histórico...</p>
                ) : analysisHistory.length === 0 ? (
                  <p style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic' }}>Nenhum registro de histórico.</p>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #E5E7EB', backgroundColor: '#F9FAFB' }}>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Data</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Analista</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Tipo</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>Ação</th>
                          <th style={{ padding: '8px', textAlign: 'left' }}>{historyScoreColumnLabel}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analysisHistory.map((r, i) => (
                          <tr key={r.id || i} style={{ borderBottom: '1px solid #E5E7EB' }}>
                            <td style={{ padding: '8px' }}>{r.created_at ? formatDate(r.created_at) : '—'}</td>
                            <td style={{ padding: '8px' }}>{r.analyst_name || '—'}</td>
                            <td style={{ padding: '8px' }}>{getAnalysisHistoryTypeLabel(r.analysis_type, r.analysis_type_label)}</td>
                            <td style={{ padding: '8px' }}>{getAnalysisHistoryActionLabel(r.action, r.action_label)}</td>
                            <td style={{ padding: '8px' }}>{r.final_score != null ? parseFloat(r.final_score).toFixed(2) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowViewModal(false); setSelectedApplicationToView(null); }}
                style={{ ...styles.button, ...styles.buttonSecondary }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
});
